import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, RotateCcw, FileText, Trash2, ArrowUpDown, ArrowUp, ArrowDown, CheckCircle2, Ban } from "lucide-react";
import { deriveOrderUnit } from "@/lib/unit";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const startOfMonthStr = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};
const endOfMonthStr = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10);
};

// 発注締切判定（ステータス表示用）
const deriveStatus = (order, schedule) => {
  if (!schedule) return { label: "未確定", className: "bg-yellow-100 text-yellow-800 border-yellow-200" };
  const now = new Date();
  const orderDl = schedule?.deadline ? new Date(schedule.deadline.replace(" ", "T")) : null;
  const changeDl = schedule?.change_deadline ? new Date(schedule.change_deadline.replace(" ", "T")) : orderDl;
  if (changeDl && now > changeDl) {
    return { label: "締切済", className: "bg-slate-200 text-slate-700 border-slate-300" };
  }
  if (orderDl && now > orderDl) {
    return { label: "確定済", className: "bg-green-100 text-green-800 border-green-200" };
  }
  return { label: "編集可", className: "bg-yellow-100 text-yellow-800 border-yellow-200" };
};

export default function OrdersView() {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [stores, setStores] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);

  const [deleteTarget, setDeleteTarget] = useState(null);

  // 店舗フィルタ + ソート
  const [otherOrders, setOtherOrders] = useState([]);
  const [storeFilter, setStoreFilter] = useState(""); // "" =未選択 | "__ALL__" =全顧客 | 店舗id
  const [sortBy, setSortBy] = useState("store"); // store | product | delivery_date | quantity
  const [sortDir, setSortDir] = useState("asc"); // asc | desc
  // 期間タブ（読み込み高速化: 納品日レンジでサーバー側絞り込み）
  const [period, setPeriod] = useState("this_week"); // past | this_week | next_week

  const ymd = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  const periodRange = (p) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dowMon = (today.getDay() + 6) % 7; // 月=0
    const sunday = new Date(today);
    sunday.setDate(today.getDate() + (6 - dowMon)); // 今週の日曜
    const nextMonday = new Date(sunday);
    nextMonday.setDate(sunday.getDate() + 1);
    if (p === "past") {
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      return { start: null, end: ymd(yesterday) };
    }
    if (p === "next_week") {
      return { start: ymd(nextMonday), end: null };
    }
    return { start: ymd(today), end: ymd(sunday) }; // this_week: 今日〜今週日曜
  };

  useEffect(() => {
    fetchData(period);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, storeFilter]);

  const fetchData = async (p = period) => {
    setLoading(true);
    try {
      // 表示モード:
      //  ""(未選択) → 発注は取得しない（全顧客・全期間を一度に出さない）
      //  それ以外   → 期間タブ(納品日レンジ)で絞り込み。特定顧客なら store_id も付与。
      const mode = storeFilter === "" ? "none" : storeFilter === "__ALL__" ? "all" : "store";
      const range = mode === "none" ? { start: null, end: null } : periodRange(p);
      const params = {
        ...(range.start ? { start: range.start } : {}),
        ...(range.end ? { end: range.end } : {}),
        ...(mode === "store" ? { store_id: storeFilter } : {}),
      };
      const rangeCfg = { ...getAuthHeaders(), params };
      const reqs = [
        mode === "none" ? Promise.resolve({ data: [] }) : axios.get(`${API}/orders`, rangeCfg),
        axios.get(`${API}/products`, getAuthHeaders()),
        axios.get(`${API}/stores`, getAuthHeaders()),
        axios.get(`${API}/delivery-schedules`, getAuthHeaders()),
        mode === "none" ? Promise.resolve({ data: [] }) : axios.get(`${API}/other-orders`, rangeCfg).catch(() => ({ data: [] })),
      ];
      const [ordersRes, productsRes, storesRes, schedulesRes, otherRes] = await Promise.all(reqs);
      setOrders(ordersRes.data);
      setProducts(productsRes.data);
      setStores(storesRes.data);
      setSchedules(schedulesRes.data);
      setOtherOrders(Array.isArray(otherRes.data) ? otherRes.data : []);
    } catch (error) {
      toast.error("データの取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/orders/${deleteTarget.id}`, getAuthHeaders());
      toast.success("発注を削除しました");
      setDeleteTarget(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "削除に失敗しました");
    }
  };

  const toggleConfirm = async (order) => {
    try {
      await axios.put(`${API}/orders/${order.id}/confirm`, {}, getAuthHeaders());
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "確定切替に失敗しました");
    }
  };

  const toggleOtherConfirm = async (oo) => {
    try {
      await axios.put(`${API}/other-orders/${oo.id}/confirm`, {}, getAuthHeaders());
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "確定切替に失敗しました");
    }
  };

  const toggleCancel = async (order) => {
    const next = !order.cancelled;
    if (
      next &&
      !window.confirm(
        "この商品を取消（差し戻し）します。確定が外れ、顧客の発注書控えに「取消」と表示されます。よろしいですか？"
      )
    )
      return;
    try {
      await axios.post(`${API}/orders/${order.id}/cancel`, { cancelled: next }, getAuthHeaders());
      toast.success(next ? "取消しました（発注書控えに反映されます）" : "取消を解除しました");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "取消処理に失敗しました");
    }
  };

  const toggleOtherCancel = async (oo) => {
    const next = !oo.cancelled;
    if (
      next &&
      !window.confirm(
        "この商品を取消（差し戻し）します。確定が外れ、顧客の発注書控えに「取消」と表示されます。よろしいですか？"
      )
    )
      return;
    try {
      await axios.post(`${API}/other-orders/${oo.id}/cancel`, { cancelled: next }, getAuthHeaders());
      toast.success(next ? "取消しました（発注書控えに反映されます）" : "取消を解除しました");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "取消処理に失敗しました");
    }
  };

  const productsMap = products.reduce((acc, p) => { acc[p.id] = p; return acc; }, {});
  const storesMap = stores.reduce((acc, s) => { acc[s.id] = s; return acc; }, {});
  const schedulesMap = schedules.reduce((acc, s) => { acc[s.delivery_date] = s; return acc; }, {});

  // フィルタ + ソート適用
  const sortedStores = useMemo(
    () => [...stores].sort((a, b) => (a.code || "").localeCompare(b.code || "")),
    [stores]
  );

  const displayedOrders = useMemo(() => {
    let list = orders;
    if (storeFilter && storeFilter !== "__ALL__") {
      list = list.filter((o) => o.store_id === storeFilter);
    }
    list = [...list].sort((a, b) => {
      let r = 0;
      const aStore = storesMap[a.store_id] || {};
      const bStore = storesMap[b.store_id] || {};
      const aProd = productsMap[a.product_id] || {};
      const bProd = productsMap[b.product_id] || {};
      switch (sortBy) {
        case "store":
          r = (aStore.code || "").localeCompare(bStore.code || "");
          if (r === 0) r = (a.delivery_date || "").localeCompare(b.delivery_date || "");
          if (r === 0) r = (aProd.code || "").localeCompare(bProd.code || "");
          break;
        case "product":
          r = (aProd.name || "").localeCompare(bProd.name || "", "ja");
          break;
        case "delivery_date":
          r = (a.delivery_date || "").localeCompare(b.delivery_date || "");
          break;
        case "quantity":
          r = (a.quantity || 0) - (b.quantity || 0);
          break;
        default:
          r = 0;
      }
      return sortDir === "asc" ? r : -r;
    });
    return list;
  }, [orders, storeFilter, sortBy, sortDir, storesMap, productsMap]);

  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortDir("asc");
    }
  };

  const sortIcon = (key) => {
    if (sortBy !== key) return <ArrowUpDown className="h-3 w-3 inline ml-1 opacity-40" />;
    return sortDir === "asc" ? (
      <ArrowUp className="h-3 w-3 inline ml-1" />
    ) : (
      <ArrowDown className="h-3 w-3 inline ml-1" />
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-stretch gap-3">
        <div className="w-1 bg-emerald-600 rounded-sm" />
        <div>
          <h2 className="text-2xl font-semibold tracking-tight text-slate-900">全発注データ</h2>
          <p className="text-sm text-slate-500 mt-1">
            発注締切を過ぎた時点で自動的に「確定済」になります。
          </p>
        </div>
      </div>

      {/* 顧客ピッカー（主役）: 顧客を選び、期間を選んで表示 */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4 flex flex-wrap items-center gap-3">
        <Label className="text-sm font-semibold text-slate-700">顧客を選択:</Label>
        <select
          data-testid="orders-store-filter"
          value={storeFilter}
          onChange={(e) => setStoreFilter(e.target.value)}
          className="h-10 px-3 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 min-w-[16rem]"
        >
          <option value="">-- 顧客を選択してください --</option>
          {sortedStores.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}（{s.code}）
            </option>
          ))}
          <option value="__ALL__">▼ すべての顧客を表示</option>
        </select>
        {storeFilter && storeFilter !== "__ALL__" && (
          <button
            data-testid="orders-store-filter-clear"
            onClick={() => setStoreFilter("")}
            className="text-xs text-slate-500 underline hover:text-slate-700"
          >
            選び直す
          </button>
        )}
        {/* 顧客/全顧客を選択したら期間タブを表示（納品日で絞り込み） */}
        {storeFilter !== "" && (
          <div className="flex flex-wrap items-center gap-2 ml-auto" data-testid="orders-period-tabs">
            <span className="text-xs text-slate-500 mr-1">期間:</span>
            {[
              { key: "past", label: "過去" },
              { key: "this_week", label: "今週" },
              { key: "next_week", label: "来週以降" },
            ].map((t) => (
              <button
                key={t.key}
                data-testid={`orders-period-${t.key}`}
                onClick={() => setPeriod(t.key)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                  period === t.key
                    ? "bg-emerald-600 text-white border-emerald-600"
                    : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {storeFilter === "" ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-lg p-10 text-center" data-testid="orders-empty-prompt">
          <p className="text-slate-600 font-medium">顧客を選択すると、その顧客の発注状況が表示されます</p>
          <p className="text-sm text-slate-400 mt-1">
            上の「顧客を選択」から店舗を選んでください。全顧客をまとめて見る場合は「▼ すべての顧客を表示」を選びます。
          </p>
        </div>
      ) : loading ? (
        <div className="text-center py-12 text-slate-500">読み込み中...</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-auto">
          {/* サマリバー */}
          <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex flex-wrap items-center gap-3">
            <span className="text-sm font-semibold text-slate-800" data-testid="orders-summary-store">
              {storeFilter === "__ALL__"
                ? "すべての顧客"
                : sortedStores.find((s) => s.id === storeFilter)?.name || "顧客"}
            </span>
            <span className="text-xs text-slate-400">
              {period === "past" ? "過去" : period === "next_week" ? "来週以降" : "今週"}
            </span>
            <span className="ml-auto text-xs text-slate-500">
              表示: <span className="font-mono font-semibold text-slate-800">{displayedOrders.length}</span> 件
            </span>
          </div>
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th
                  onClick={() => toggleSort("store")}
                  data-testid="sort-store"
                  className="h-10 px-4 text-left font-medium text-slate-500 text-xs uppercase cursor-pointer select-none hover:bg-slate-100"
                >
                  店舗{sortIcon("store")}
                </th>
                <th
                  onClick={() => toggleSort("product")}
                  data-testid="sort-product"
                  className="h-10 px-4 text-left font-medium text-slate-500 text-xs uppercase cursor-pointer select-none hover:bg-slate-100"
                >
                  商品{sortIcon("product")}
                </th>
                <th
                  onClick={() => toggleSort("delivery_date")}
                  data-testid="sort-delivery_date"
                  className="h-10 px-4 text-left font-medium text-slate-500 text-xs uppercase cursor-pointer select-none hover:bg-slate-100"
                >
                  納品日{sortIcon("delivery_date")}
                </th>
                <th
                  onClick={() => toggleSort("quantity")}
                  data-testid="sort-quantity"
                  className="h-10 px-4 text-right font-medium text-slate-500 text-xs uppercase cursor-pointer select-none hover:bg-slate-100"
                >
                  数量{sortIcon("quantity")}
                </th>
                <th className="h-10 px-4 text-center font-medium text-slate-500 text-xs uppercase">ステータス</th>
                <th className="h-10 px-4 text-center font-medium text-slate-500 text-xs uppercase">確定(デスク)</th>
                <th className="h-10 px-4 text-center font-medium text-slate-500 text-xs uppercase w-20">操作</th>
              </tr>
            </thead>
            <tbody>
              {displayedOrders.map((order) => {
                const product = productsMap[order.product_id] || {};
                const store = storesMap[order.store_id] || {};
                const schedule = schedulesMap[order.delivery_date];
                const statusInfo = deriveStatus(order, schedule);
                return (
                  <tr key={order.id} className={`border-b border-slate-100 hover:bg-slate-50/50 ${order.cancelled ? "bg-red-50/50" : ""}`}>
                    <td className="p-2 px-4 align-middle text-sm">
                      <div className="font-medium">{store.name}</div>
                      <div className="text-[10px] text-slate-500 font-mono">{store.code}</div>
                    </td>
                    <td className="p-2 px-4 align-middle text-sm">
                      <span className={order.cancelled ? "line-through text-slate-400" : ""}>{product.name}</span>
                      {order.cancelled && (
                        <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-700 border border-red-300" data-testid={`order-cancelled-badge-${order.id}`}>
                          取消
                        </span>
                      )}
                    </td>
                    <td className="p-2 px-4 align-middle text-sm font-mono">{order.delivery_date}</td>
                    <td className="p-2 px-4 align-middle text-sm font-mono text-right">
                      {order.quantity.toFixed(2)} {order.unit || deriveOrderUnit(product)}
                    </td>
                    <td className="p-2 px-4 align-middle text-center">
                      <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border ${statusInfo.className}`}>
                        {statusInfo.label}
                      </span>
                    </td>
                    <td className="p-2 px-4 align-middle text-center">
                      <button
                        type="button"
                        onClick={() => toggleConfirm(order)}
                        data-testid={`toggle-confirm-${order.id}`}
                        title={order.confirmed ? "確定を解除" : "確定する（デスク送信対象）"}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border transition-colors ${
                          order.confirmed
                            ? "bg-indigo-100 text-indigo-800 border-indigo-300 hover:bg-indigo-200"
                            : "bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100"
                        }`}
                      >
                        {order.confirmed ? <CheckCircle2 className="h-3 w-3" /> : null}
                        {order.confirmed ? "確定済" : "未確定"}
                      </button>
                    </td>
                    <td className="p-2 px-4 align-middle text-center">
                      <div className="inline-flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => toggleCancel(order)}
                          data-testid={`cancel-order-${order.id}`}
                          title={order.cancelled ? "取消を解除" : "取消（差し戻し）"}
                          className={`inline-flex items-center justify-center h-8 w-8 rounded-md transition-colors ${
                            order.cancelled
                              ? "text-emerald-600 hover:bg-emerald-50"
                              : "text-orange-600 hover:bg-orange-50"
                          }`}
                        >
                          {order.cancelled ? <RotateCcw className="h-4 w-4" /> : <Ban className="h-4 w-4" />}
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteTarget({ ...order, product, store })}
                          data-testid={`delete-order-${order.id}`}
                          title="発注を削除"
                          className="inline-flex items-center justify-center h-8 w-8 rounded-md text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {displayedOrders.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              {orders.length === 0 ? "発注データがありません" : "該当する発注はありません"}
            </div>
          )}
        </div>
      )}

      {/* リストなし発注 */}
      {!loading && storeFilter !== "" && (() => {
        const filteredOther = otherOrders.filter((o) =>
          storeFilter === "__ALL__" || o.store_id === storeFilter
        );
        const sortedOther = [...filteredOther].sort((a, b) => {
          const aS = storesMap[a.store_id] || {};
          const bS = storesMap[b.store_id] || {};
          const r = (aS.code || "").localeCompare(bS.code || "");
          return r !== 0 ? r : (a.delivery_date || "").localeCompare(b.delivery_date || "");
        });
        if (sortedOther.length === 0) return null;
        return (
          <div className="bg-white border border-amber-200 rounded-lg shadow-sm overflow-hidden">
            <div className="px-4 py-3 bg-amber-50 border-b border-amber-200 flex items-center justify-between gap-3 flex-wrap">
              <h3 className="text-sm font-semibold text-amber-800 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                リストなし発注
              </h3>
              <span className="text-xs text-amber-700 font-mono">{sortedOther.length}件</span>
            </div>
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="h-9 px-4 text-left font-medium text-slate-500 text-xs uppercase">店舗</th>
                  <th className="h-9 px-4 text-left font-medium text-slate-500 text-xs uppercase">商品名</th>
                  <th className="h-9 px-4 text-left font-medium text-slate-500 text-xs uppercase">納品日</th>
                  <th className="h-9 px-4 text-right font-medium text-slate-500 text-xs uppercase">数量</th>
                  <th className="h-9 px-4 text-left font-medium text-slate-500 text-xs uppercase">備考</th>
                  <th className="h-9 px-4 text-center font-medium text-slate-500 text-xs uppercase">確定(デスク)</th>
                </tr>
              </thead>
              <tbody>
                {sortedOther.map((oo) => {
                  const store = storesMap[oo.store_id] || {};
                  return (
                    <tr key={oo.id} data-testid={`admin-other-${oo.id}`} className={`border-b border-slate-100 hover:bg-amber-50/30 ${oo.cancelled ? "bg-red-50/50" : ""}`}>
                      <td className="p-2 px-4 align-middle text-sm">
                        <div className="font-medium">{store.name || "—"}</div>
                        <div className="text-[10px] text-slate-500 font-mono">{store.code}</div>
                      </td>
                      <td className="p-2 px-4 align-middle text-sm font-medium">
                        <span className={oo.cancelled ? "line-through text-slate-400" : ""}>{oo.product_name || "—"}</span>
                        {oo.cancelled && (
                          <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-700 border border-red-300" data-testid={`other-cancelled-badge-${oo.id}`}>
                            取消
                          </span>
                        )}
                      </td>
                      <td className="p-2 px-4 align-middle text-sm font-mono">{oo.delivery_date}</td>
                      <td className="p-2 px-4 align-middle text-sm font-mono text-right">
                        {Number(oo.quantity || 0).toFixed(2)} {oo.unit || "g"}
                      </td>
                      <td className="p-2 px-4 align-middle text-sm text-slate-600 truncate max-w-md">{oo.notes || ""}</td>
                      <td className="p-2 px-4 align-middle text-center">
                        <div className="inline-flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => toggleOtherConfirm(oo)}
                            data-testid={`toggle-other-confirm-${oo.id}`}
                            title={oo.confirmed ? "確定を解除" : "確定する（デスク送信対象）"}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border transition-colors ${
                              oo.confirmed
                                ? "bg-indigo-100 text-indigo-800 border-indigo-300 hover:bg-indigo-200"
                                : "bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100"
                            }`}
                          >
                            {oo.confirmed ? <CheckCircle2 className="h-3 w-3" /> : null}
                            {oo.confirmed ? "確定済" : "未確定"}
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleOtherCancel(oo)}
                            data-testid={`cancel-other-${oo.id}`}
                            title={oo.cancelled ? "取消を解除" : "取消（差し戻し）"}
                            className={`inline-flex items-center justify-center h-7 w-7 rounded-md transition-colors ${
                              oo.cancelled
                                ? "text-emerald-600 hover:bg-emerald-50"
                                : "text-orange-600 hover:bg-orange-50"
                            }`}
                          >
                            {oo.cancelled ? <RotateCcw className="h-3.5 w-3.5" /> : <Ban className="h-3.5 w-3.5" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })()}

      {/* 削除確認ダイアログ */}
      <Dialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <DialogContent data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700">発注を削除しますか？</DialogTitle>
            <DialogDescription className="pt-2 text-slate-600">
              この操作は取り消せません。本当に削除してもよろしいですか？
            </DialogDescription>
          </DialogHeader>
          {deleteTarget && (
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm space-y-1">
              <div><span className="text-slate-500">店舗:</span> <span className="font-medium">{deleteTarget.store?.name || "-"}</span></div>
              <div><span className="text-slate-500">商品:</span> <span className="font-medium">{deleteTarget.product?.name || "-"}</span></div>
              <div><span className="text-slate-500">納品日:</span> <span className="font-mono">{deleteTarget.delivery_date}</span></div>
              <div><span className="text-slate-500">数量:</span> <span className="font-mono">{deleteTarget.quantity?.toFixed(2)} {deleteTarget.unit || deriveOrderUnit(deleteTarget.product)}</span></div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} data-testid="delete-cancel">
              キャンセル
            </Button>
            <Button onClick={confirmDelete} data-testid="delete-confirm" className="bg-red-600 text-white hover:bg-red-700">
              <Trash2 className="h-4 w-4 mr-2" />
              削除する
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
